import { DEPORTED_DETAILS } from '@/modules/background_check/bgc-static'
import { BackgroundCheck, Case } from '@/modules/models'
import axios from 'axios'
import _pickBy from 'lodash/pickBy'
import { action, createModule, mutation } from 'vuex-class-component'


const VuexModule = createModule({
	namespaced: 'BackgroundChecks',
	strict: false,
})

const toIsoOrNull = (value: any): string | null => {
	if (!value) return null
	if (typeof value === 'string') return value
	if (typeof value?.toISO === 'function') return value.toISO()
	return String(value)
}

const normalizeRazon = (value: any): string | null => {
	if (value === null || value === undefined) return null
	if (typeof value === 'string') return value
	if (Array.isArray(value)) {
		const flattened = value.filter(v => v !== null && v !== undefined).map(v => String(v))
		return flattened.length ? flattened.join(' | ') : null
	}
	if (typeof value === 'object') {
		if (Object.prototype.hasOwnProperty.call(value, '0') && Object.keys(value).length === 1) {
			const onlyValue = value['0']
			return onlyValue === null || onlyValue === undefined ? null : String(onlyValue)
		}
		const flattened = Object.values(value)
			.filter(v => v !== null && v !== undefined)
			.map(v => String(v))
		return flattened.length ? flattened.join(' | ') : null
	}
	return String(value)
}
const toUpdatePayload = (bgc: any, detailsOverride: any = null) => {
	const details = detailsOverride || bgc?.details || {}
	
	// Asegurar estructura de DO BGCs para que pase la validación estricta de Pydantic
	if (bgc.country_code === 'DO' || details.JUD_PENAL || details.PGR || details.INTRANT || bgc.person?.country_code === 'DO') {
		if (!details.JUD_PENAL) details.JUD_PENAL = { cases: [] }
		if (!details.PGR) details.PGR = { good_standing: null }
		if (!details.INTRANT) details.INTRANT = { infraction_count: null }
	}
	
	// Limpieza de objetos reactivos de Vue que causan error 422 en el backend
	const safeDetails = JSON.parse(JSON.stringify(details, (_, value) => {
		if (value && typeof value.toISO === 'function') return value.toISO()
		if (value && typeof value === 'object' && value.__ob__) {
			const cleanObj = { ...value }
			delete cleanObj.__ob__
			return cleanObj
		}
		return value
	}))
	if (Array.isArray(safeDetails?.JUD_PENAL?.cases)) {
		safeDetails.JUD_PENAL.cases = safeDetails.JUD_PENAL.cases.map((c: any) => ({
			...c,
			razon: normalizeRazon(c?.razon),
		}))
	}
	
	return {
		id: bgc.id,
		status: bgc.status,
		run_start: toIsoOrNull(bgc.run_start),
		completed_on: toIsoOrNull(bgc.completed_on),
		details: safeDetails,
	}
}

const toApprovedUpdatePayload = (bgc: any) => {
	return toUpdatePayload(bgc, {
		...(bgc?.details || {}),
		screening_done: true,
	})
}

export default class BackgroundChecksStore extends VuexModule {
	backgroundChecks: { [key: string]: BackgroundCheck } = {}
	totalItems = 0
	currentPage = 1
	itemsPerPage = 300

	@mutation
	setBackgroundChecks(payload: { items: any[], total: number }) {
		this.backgroundChecks = payload.items.reduce((bgcs, rawBgc) => {
			const bgc = {
				...rawBgc,
				// Country endpoint returns run_start; model expects created_at.
				created_at: rawBgc.created_at || rawBgc.run_start || null,
			}

			return {
				...bgcs,
				[bgc.id]: new BackgroundCheck(bgc),
			}
		}, {})
		this.totalItems = payload.total
	}

	@mutation
	setPage(page: number) {
		this.currentPage = Math.max(1, page)
	}

	@mutation
	setItemsPerPage(perPage: number) {
		this.itemsPerPage = Math.max(1, perPage)
	}

	@mutation
	updateBackgroundCheck(backgroundCheck: BackgroundCheck){
		this.backgroundChecks[backgroundCheck.id] = backgroundCheck
	}

	@mutation
	removeBackgroundChecks(ids: string[]){
		this.backgroundChecks = _pickBy(this.backgroundChecks, v => !ids.includes(v.id))
	}

	@mutation
	approveBackgroundChecks(backgroundChecks: BackgroundCheck[]){
		const ids = backgroundChecks.map(b => b.id)
		this.backgroundChecks = Object.values(this.backgroundChecks).reduce((bgcs, bgc) => {
			if(ids.includes(bgc.id))
				bgc.status = 'passed'

			return {
				...bgcs,
				[bgc.id]: bgc
			}
		}, {})
	}

	@action
	async loadBackgroundChecks() {
		const normalizeResponse = (data: any): { items: any[], total: number } => {
			if (Array.isArray(data)) {
				return { items: data, total: data.length }
			}

			return {
				items: data?.items || [],
				total: data?.total || 0,
			}
		}

		const apiLimit = 1000
		let skip = 0
		let allItems: any[] = []

		// Pull all DO pending BGCs in chunks and paginate only on frontend.
		while (true) {
			// El orden exacto de los parametros es estricto: skip, limit, status
			const { data } = await axios.get(`/api/v1/background-checks/do?skip=${skip}&limit=${apiLimit}&status=pending`)

			const currentItems = normalizeResponse(data).items
			allItems = allItems.concat(currentItems)

			if (currentItems.length < apiLimit) break
			skip += apiLimit
		}
		const offset = (this.currentPage - 1) * this.itemsPerPage
		const paginatedItems = allItems.slice(offset, offset + this.itemsPerPage)
		this.setBackgroundChecks({ items: paginatedItems, total: allItems.length })
	}

	@action
	async sendBackgroundCheck(payload: { id: string, photos: File[] }){
		const { id, photos } = payload

		const formData = new FormData()
		photos.forEach(p => formData.append('files', p))

		let bgc = this.backgroundChecks[id].clone()
		bgc.details.JUD_PENAL.cases = bgc.details.JUD_PENAL.cases.map(c => {
			if(c.deported){
				return new Case({ details: DEPORTED_DETAILS, estado_actual: "Deported", deported: true })
			}

			return c
		})

		const toSend = toUpdatePayload(bgc)

		const promises = [
			axios.put('/api/v1/background-checks/update_many', [toSend]),
			...photos.length ? [axios.post(`/api/v1/background-checks/${toSend.id}/files`, formData, { headers: {'Content-Type': 'multipart/form-data' } })] : []
		]

		await Promise.all(promises)
		this.removeBackgroundChecks([id])
	}

	@action
	async sendApprovedBackgroundChecks(){
		const approvedToSend = Object.values(this.backgroundChecks)
			.filter(bgc => bgc.status == 'passed')
			.map(bgc => toApprovedUpdatePayload(bgc))
			
		if (approvedToSend.length === 0) return

		await axios.put('/api/v1/background-checks/update_many', approvedToSend)
		this.removeBackgroundChecks(approvedToSend.map(bgc => bgc.id))
	}
}
